'use client'

import { useMemo } from 'react'
import {
  Font,
  Image,
  Line,
  Path,
  Rect,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer'

import { rotorStyles } from '@/components/organisms/Viewer/styles'
import { transformLineBreaks } from '@/components/organisms/Viewer/utils'
import {
  formatDimensions_Rotor,
  getCustomProductName,
  getQuantityLabel,
  getRotorCustomLabel,
  getTranslation,
} from '@/utils'
import { imgPath } from '@/services/adapters'
import { formatShapeLabelsCompact } from '@/utils/shape.utils'
import { createLocalizedCatalogNodeText } from '@/components/organisms/CatalogEditor/utils/catalogNodeText'

// Register the Inter font
Font.register({
  family: 'Inter',
  fonts: [
    {
      src: '/fonts/Inter_18pt-ExtraLight.ttf',
      fontWeight: 'light',
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter_18pt-ExtraLightItalic.ttf',
      fontWeight: 'light',
      fontStyle: 'italic',
    },
    {
      src: '/fonts/Inter_18pt-Regular.ttf',
      fontWeight: 'normal',
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter_18pt-Medium.ttf',
      fontWeight: 'medium',
      fontStyle: 'normal',
    },
    {
      src: '/fonts/Inter_18pt-SemiBold.ttf',
      fontWeight: 'semibold',
      fontStyle: 'normal',
    },
  ],
})

const HeaderItem = ({ header, isFirst, context, locale, fallbackLocales }) => {
  const categoryOptions =
    context?.propertyOptions?.category ?? context?.category

  const lot = useMemo(() => {
    return createLocalizedCatalogNodeText({
      value: header.lot,
      categoryOptions,
      categoryOptionsByID: context?.optionsByID?.category,
      type: 'lot',
      locale,
      categoryRefData: context?.refDataByKey?.category,
    })
  }, [
    categoryOptions,
    context?.optionsByID?.category,
    context?.refDataByKey?.category,
    header.lot,
    locale,
  ])

  const category = useMemo(() => {
    return createLocalizedCatalogNodeText({
      value: header.category,
      categoryOptions,
      categoryOptionsByID: context?.optionsByID?.category,
      type: 'category',
      locale,
      categoryRefData: context?.refDataByKey?.category,
    })
  }, [
    categoryOptions,
    context?.optionsByID?.category,
    context?.refDataByKey?.category,
    header.category,
    locale,
  ])

  const descriptionLabel = useMemo(() => {
    const translated = getTranslation(
      header,
      'description',
      locale,
      fallbackLocales,
    )
    return transformLineBreaks(translated)
  }, [fallbackLocales, header, locale])

  return (
    <View
      style={{
        ...rotorStyles.header,
        ...(!isFirst && !lot && { minHeight: '3.175cm' }),
      }}
    >
      {!!lot && <Text style={rotorStyles.headerTitle}>{lot}</Text>}
      <Text style={rotorStyles.headerSubtitle}>{category}</Text>
      {!!descriptionLabel?.length && (
        <Text style={rotorStyles.headerDescription}>{descriptionLabel}</Text>
      )}
    </View>
  )
}

const RotorDivider = () => <View style={rotorStyles.divider} />

type InventoryItemProps = {
  product: any
  isFirst: boolean
  locale: string
  project: string
  fallbackLocales?: readonly string[]
}

const InventoryItem = ({
  product,
  isFirst,
  locale,
  fallbackLocales,
}: InventoryItemProps) => {
  const { pictures, shapeID, channels, reemploymentIndex } = product || {}

  const imageUrl = useMemo(
    () => imgPath(pictures?.[0], 'thumbnail'),
    [pictures],
  )

  const descriptionLabel = useMemo(() => {
    const translation = getTranslation(
      product,
      'description',
      locale,
      fallbackLocales,
    )
    return transformLineBreaks(translation)
  }, [fallbackLocales, product, locale])

  // TODO: translate lots and categories

  const productLabel = useMemo(
    () => getCustomProductName(product, locale, fallbackLocales),
    [fallbackLocales, product, locale],
  )

  const quantityLabel = useMemo(() => {
    return getQuantityLabel(product)
  }, [product])

  const channelsLabel = useMemo(
    () =>
      channels && Array.isArray(channels)
        ? channels?.map((channel) => channel.label).join(', ')
        : '',
    [channels],
  )

  const dimensionsLabel = useMemo(
    () => formatDimensions_Rotor(product)?.label,
    [product],
  )

  const shapesLabel = useMemo(
    () => formatShapeLabelsCompact(shapeID),
    [shapeID],
  )

  const customIDLabel = useMemo(() => getRotorCustomLabel(product), [product])

  const iconLabel = useMemo(() => {
    switch (locale) {
      case 'en':
        return 'See detailed information'
      case 'nl':
        return 'Zie gedetailleerde fiche'
      case 'fr':
      default:
        return 'Voir fiche détaillée'
    }
  }, [locale])

  return (
    <View
      style={{
        ...rotorStyles.item,
        ...(isFirst && { borderTop: '0.75 solid #000000' }),
      }}
    >
      <View style={rotorStyles.imageContainer}>
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <Image src={`${imageUrl}`} style={rotorStyles.image} />
      </View>
      <View style={{ ...rotorStyles.list, position: 'relative' }}>
        <Text style={rotorStyles.reference}>{customIDLabel}</Text>
        <View style={rotorStyles.row}>
          <Text style={rotorStyles.title}>{productLabel}</Text>
          <Text style={rotorStyles.reemployment}>
            {reemploymentIndex?.label}
          </Text>
        </View>
        <RotorDivider />
        <View style={rotorStyles.row}>
          <Text style={{ ...rotorStyles.text, fontWeight: 500 }}>
            {quantityLabel}
          </Text>
          <Text style={rotorStyles.text}>{channelsLabel}</Text>
        </View>
        <RotorDivider />
        <View style={rotorStyles.row}>
          <Text style={rotorStyles.text}>{dimensionsLabel}</Text>
          {!!shapesLabel && (
            <View style={rotorStyles.space}>
              <LocationPin />
              <Text style={rotorStyles.text}>{shapesLabel}</Text>
            </View>
          )}
        </View>
        <RotorDivider />
        <View style={{ ...rotorStyles.list, paddingHorizontal: 4 }}>
          <Text style={{ ...rotorStyles.text, marginVertical: '4px' }}>
            {descriptionLabel}
          </Text>
        </View>
        {!!product.detailedSheet && (
          <View
            style={{
              ...rotorStyles.row,
              justifyContent: 'flex-start',
              alignItems: 'flex-end',
              position: 'absolute',
              bottom: 0,
              left: 0,
              gap: 3,
            }}
          >
            <DetailedSheetIcon />
            <Text
              style={{
                ...rotorStyles.boldText,
                fontSize: 10,
                marginBottom: -2,
              }}
            >
              {iconLabel}
            </Text>
          </View>
        )}
      </View>
    </View>
  )
}

type RotorDefaultProps = {
  items: any[]
  project: string
  locale: string
  context: any
  fallbackLocales?: readonly string[]
}

export const RotorDefault = ({
  items = [],
  project,
  locale,
  context,
  fallbackLocales,
}: RotorDefaultProps) => (
  <View style={[rotorStyles.list]}>
    {items?.map((item, index) =>
      item?.type === 'HEADER' ? (
        <HeaderItem
          key={`header-${item?.category}-${item?.lot || 'no-lot'}-${index}`}
          isFirst={index === 0}
          header={item}
          context={context}
          locale={locale}
          fallbackLocales={fallbackLocales}
        />
      ) : (
        <InventoryItem
          key={item?.id}
          product={item}
          isFirst={index === 0}
          locale={locale}
          project={project}
          fallbackLocales={fallbackLocales}
        />
      ),
    )}
  </View>
)

const LocationPin = () => (
  <Svg viewBox="0 0 24 24" width="12" height="12">
    <Path
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
      stroke="#000"
      strokeWidth={1}
      strokeLinejoin="round"
    />
  </Svg>
)

const DetailedSheetIcon = () => (
  <Svg width="9.5" height="12" viewBox="0 0 19 24">
    <Rect
      x="0.25"
      y="0.25"
      width="18.5"
      height="23.5"
      stroke="black"
      strokeWidth="0.5"
    />
    <Rect
      x="10.4"
      y="3.4"
      width="5.2"
      height="4.2"
      stroke="black"
      strokeWidth="0.8"
    />
    <Line x1="16" y1="11.4" x2="3" y2="11.4" stroke="black" strokeWidth="0.8" />
    <Line x1="16" y1="15.4" x2="3" y2="15.4" stroke="black" strokeWidth="0.8" />
    <Line x1="16" y1="19.4" x2="3" y2="19.4" stroke="black" strokeWidth="0.8" />
    <Rect
      x="0.4"
      y="0.4"
      width="18.2"
      height="23.2"
      stroke="black"
      strokeWidth="0.8"
    />
  </Svg>
)
